// AGC: User Profile + Team Management service
// AGC: Extracted from firebase-auth.ts for separation of concerns
// AGC: Handles all Firestore user document CRUD and team member operations

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  limit,
  serverTimestamp,
} from 'firebase/firestore'
import { User } from 'firebase/auth'
import { db } from '../lib/firebase'
import { getIdToken } from './firebase-auth'
import type { UserProfile, UserRole, UserSubtype } from '../types/user'
import { handleFirestoreError, isTransientNetworkError } from '../utils/firestoreErrorHandler'

// ========================================
// AGC: USER PROFILE MANAGEMENT (FIRESTORE)
// ========================================

export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  try {
    const docRef = doc(db, 'users', uid)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      const data = docSnap.data()
      return {
        uid,
        email: data.email || null,
        displayName: data.displayName || null,
        photoURL: data.photoURL || null,
        phoneNumber: data.phoneNumber || null,
        role: data.role || 'user',
        parentManagerId: data.parentManagerId || null,
        createdBy: data.createdBy || null,
        biometricEnabled: data.biometricEnabled || false,
        biometricCredentialId: data.biometricCredentialId || null,
        isActive: data.isActive !== false,
        lastLoginAt: data.lastLoginAt?.toDate?.()?.toISOString() || data.lastLoginAt || null,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt || new Date().toISOString(),
        whatsappPhone: data.whatsappPhone || null,
        location: data.location || null,
        farmName: data.farmName || null,
        farmArea: data.farmArea || null,
        // AGC: Farm-scoped role for sub-users (set by invite accept / role change).
        // AGC: Fallback to legacy nested `profile.farmRole` for users written by pre-v3.9.33
        // AGC: backend authMiddleware (wrote nested; every reader expected flat).
        farmRole: data.farmRole || (data.profile as Record<string, unknown> | undefined)?.farmRole || null,
        parentFarmId: data.parentFarmId || null,
      } as UserProfile
    }
    return null
  } catch (error) {
    console.error('❌ AGC: Error getting user profile:', error)
    return null
  }
}

// AGC: Emails auto-granted admin role on first profile creation (system operators)
const AUTO_ADMIN_EMAILS = ['farmpromapa@gmail.com', 'junioradrover@gmail.com']

export const createUserProfile = async (
  user: User,
  additionalData?: Partial<UserProfile>
): Promise<UserProfile> => {
  const isAutoAdmin = AUTO_ADMIN_EMAILS.includes((user.email || '').toLowerCase())
  // AGC: 3.9.28 — reverted the defensive farmRole write from 3.9.27. It was breaking Google
  // AGC: signup on Play Store builds (account creation silently failed). Backend lazy migration
  // AGC: in authMiddleware.js + mapLegacyToFarmRole fix handles self-signup → 'owner' correctly
  // AGC: on the first authenticated backend request, so the wizard fix still holds.
  const userProfile = {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName || additionalData?.displayName || null,
    photoURL: user.photoURL || additionalData?.photoURL || null,
    phoneNumber: user.phoneNumber || additionalData?.phoneNumber || null,
    role: isAutoAdmin ? 'admin' : (additionalData?.role || 'user'),
    parentManagerId: additionalData?.parentManagerId || null,
    createdBy: additionalData?.createdBy || null,
    biometricEnabled: false,
    biometricCredentialId: null,
    isActive: true,
    lastLoginAt: new Date().toISOString(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }

  const docRef = doc(db, 'users', user.uid)
  await setDoc(docRef, userProfile)

  return {
    ...userProfile,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  } as UserProfile
}

export const updateUserProfile = async (uid: string, updates: Partial<UserProfile>): Promise<void> => {
  const docRef = doc(db, 'users', uid)
  await updateDoc(docRef, { ...updates, updatedAt: serverTimestamp() })
}

export const updateLastLogin = async (uid: string): Promise<void> => {
  try {
    const docRef = doc(db, 'users', uid)
    await updateDoc(docRef, { lastLoginAt: serverTimestamp(), updatedAt: serverTimestamp() })
  } catch (error) {
    if (isTransientNetworkError(error)) return
    handleFirestoreError(error, 'updateLastLogin', false)
  }
}

export const updateBiometricStatus = async (uid: string, enabled: boolean, credentialId?: string): Promise<void> => {
  const docRef = doc(db, 'users', uid)
  await updateDoc(docRef, {
    biometricEnabled: enabled,
    biometricCredentialId: credentialId || null,
    updatedAt: serverTimestamp(),
  })
}

// AGC: Admin operations go through backend API (Firestore rules only allow self-writes)
export const updateUserRole = async (uid: string, role: UserRole): Promise<void> => {
  const token = await getIdToken()
  if (!token) throw new Error('Not authenticated')

  const { getBackendUrl } = await import('../utils/backendUrl')
  const response = await fetch(`${getBackendUrl()}/api/v1/auth/user/${uid}/role`, {
    method: 'PUT',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.message || 'Failed to update user role')
  }
}

export const deactivateUser = async (uid: string): Promise<void> => {
  const token = await getIdToken()
  if (!token) throw new Error('Not authenticated')

  const { getBackendUrl } = await import('../utils/backendUrl')
  const response = await fetch(`${getBackendUrl()}/api/v1/auth/user/${uid}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.message || 'Failed to deactivate user')
  }
}

export const reactivateUser = async (uid: string): Promise<void> => {
  const token = await getIdToken()
  if (!token) throw new Error('Not authenticated')

  const { getBackendUrl } = await import('../utils/backendUrl')
  const response = await fetch(`${getBackendUrl()}/api/v1/auth/user/${uid}/reactivate`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
  })
  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.message || 'Failed to reactivate user')
  }
}

// ========================================
// AGC: TEAM MEMBER MANAGEMENT
// ========================================

export const getUsersByManager = async (managerId: string): Promise<UserProfile[]> => {
  try {
    const token = await getIdToken()
    if (!token) return []

    const { getBackendUrl } = await import('../utils/backendUrl')
    const backendUrl = getBackendUrl()

    const response = await fetch(`${backendUrl}/api/v1/auth/my-users`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    })

    if (!response.ok) return []

    const data = await response.json()
    return (data.users || []).map((u: Record<string, unknown>) => ({
      uid: u.uid, email: u.email || null, displayName: u.displayName || null,
      photoURL: u.photoURL || null, phoneNumber: u.phoneNumber || null,
      role: u.role || 'user', subtype: u.subtype || null,
      parentManagerId: u.parentManagerId || null, createdBy: u.createdBy || null,
      biometricEnabled: u.biometricEnabled || false, biometricCredentialId: u.biometricCredentialId || null,
      isActive: u.isActive !== false,
      lastLoginAt: u.lastLoginAt || null, createdAt: u.createdAt || new Date().toISOString(),
      updatedAt: u.updatedAt || new Date().toISOString(),
      whatsappPhone: u.whatsappPhone || null, location: u.location || null,
      farmName: u.farmName || null, farmArea: u.farmArea || null,
    } as UserProfile))
  } catch (error) {
    console.error('❌ AGC: Error getting users by manager:', error)
    return []
  }
}

export const getAllUsers = async (): Promise<UserProfile[]> => {
  try {
    const token = await getIdToken()
    if (!token) return []

    const { getBackendUrl: getBEUrl } = await import('../utils/backendUrl')
    const backendUrl = getBEUrl()

    const response = await fetch(`${backendUrl}/api/v1/auth/users`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    })

    if (!response.ok) return []
    const data = await response.json()
    if (!data.success) return []

    return (data.users || []).map((u: Record<string, unknown>) => ({
      uid: u.uid, email: u.email || null, displayName: u.displayName || null,
      photoURL: null, phoneNumber: null, role: u.role || 'user',
      // AGC: Farm-scoped role and subtype for admin panel display
      farmRole: u.farmRole || undefined,
      subtype: u.subtype || null,
      parentManagerId: u.parentManagerId || null, createdBy: u.createdBy || null,
      biometricEnabled: false, biometricCredentialId: null,
      isActive: u.isActive !== false,
      lastLoginAt: u.lastLoginAt || null, createdAt: u.createdAt || null, updatedAt: null,
      whatsappPhone: null, location: null, farmName: null, farmArea: null,
    } as unknown as UserProfile))
  } catch (error) {
    console.error('❌ AGC: Error getting all users:', error)
    return []
  }
}

export const getTeamMemberCount = async (managerId: string): Promise<number> => {
  try {
    const usersRef = collection(db, 'users')
    const q = query(usersRef, where('createdBy', '==', managerId), limit(100))
    const snapshot = await getDocs(q)
    return snapshot.size
  } catch (error) {
    if (isTransientNetworkError(error)) return 0
    handleFirestoreError(error, 'getTeamMemberCount', false)
    return 0
  }
}

export interface CreateTeamMemberData {
  email: string
  password: string
  displayName: string
  role: 'submanager' | 'user'
  subtype?: UserSubtype
  phoneNumber?: string
}

export const createTeamMember = async (
  managerUid: string,
  memberData: CreateTeamMemberData
): Promise<{ success: boolean; userId?: string; error?: string }> => {
  try {
    const token = await getIdToken()
    if (!token) return { success: false, error: 'No auth token available' }

    const { getBackendUrl: getBEUrl2 } = await import('../utils/backendUrl')
    const backendUrl = getBEUrl2()

    const response = await fetch(`${backendUrl}/api/v1/auth/create-team-member`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...memberData, createdBy: managerUid, parentManagerId: managerUid }),
    })

    const data = await response.json()
    if (!response.ok) return { success: false, error: data.message || 'Error creating team member' }
    return { success: true, userId: data.userId }
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : 'Error creating team member'
    return { success: false, error: msg }
  }
}

export const updateTeamMemberSubtype = async (uid: string, subtype: UserSubtype): Promise<void> => {
  const docRef = doc(db, 'users', uid)
  await updateDoc(docRef, { subtype, updatedAt: serverTimestamp() })
}
